/**
 * ShortCircuit themes. The colours themselves live in tokens.css as CSS
 * custom properties per [data-theme]; this module is the list the picker
 * shows and the logic that puts a theme on <html>.
 *
 * index.html repeats readStoredTheme/resolveTheme/applyTheme in a tiny
 * inline script so the stored theme is applied before the bundle loads.
 * Keep the two in step.
 */

export const STORAGE_KEY = 'sc_theme'
export const DEFAULT_THEME = 'system'

// `swatch` = [page, chrome, accent] for the picker preview.
export const THEMES = Object.freeze([
  { id: 'light', label: 'Light', swatch: ['#ffffff', '#2d3436', '#2563eb'] },
  { id: 'dark', label: 'Dark', swatch: ['#0f1115', '#1e272e', '#60a5fa'] },
  { id: 'system', label: 'System', swatch: ['#ffffff', '#0f1115', '#2563eb'] },
  { id: 'ms-rt', label: 'MS-RT', swatch: ['#13100d', '#0c0a08', '#1ab8ec'] },
  { id: 'edc', label: 'EDC', swatch: ['#ffffff', '#202426', '#4a2939'] },
])

const IDS = new Set(THEMES.map((t) => t.id))
const DARK_THEMES = new Set(['dark', 'ms-rt'])

export function isThemeId(value) {
  return IDS.has(value)
}

/** The theme actually shown: System becomes Light or Dark. */
export function resolveTheme(theme, prefersDark) {
  if (theme === 'system') return prefersDark ? 'dark' : 'light'
  return isThemeId(theme) ? theme : 'light'
}

export function isDarkTheme(resolved) {
  return DARK_THEMES.has(resolved)
}

export function readStoredTheme(storage = window.localStorage) {
  try {
    const value = storage.getItem(STORAGE_KEY)
    return isThemeId(value) ? value : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

/**
 * Light is the :root default, so it carries no data-theme attribute.
 * The `dark` class keeps Tailwind `dark:` variants in step.
 */
export function applyTheme(resolved, root = document.documentElement) {
  if (resolved === 'light') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', resolved)
  const dark = isDarkTheme(resolved)
  root.classList.toggle('dark', dark)
  root.style.colorScheme = dark ? 'dark' : 'light'
}
